// src/components/auth/LoginPage.js

import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
// import { FcGoogle } from 'react-icons/fc';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Lock, Mail } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';

const LoginPage = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const { login } = useAuth(); // Removed googleLogin
    const { executeRecaptcha } = useGoogleReCaptcha();
    const [keyboardOffset, setKeyboardOffset] = useState(0);

    useEffect(() => {
        if (Capacitor.isNativePlatform()) {
            let showHandle;
            let hideHandle;

            Keyboard.addListener('keyboardWillShow', (info) => {
                setKeyboardOffset(info.keyboardHeight);
            }).then((handle) => { showHandle = handle; });

            Keyboard.addListener('keyboardWillHide', () => {
                setKeyboardOffset(0);
            }).then((handle) => { hideHandle = handle; });

            return () => {
                showHandle && showHandle.remove();
                hideHandle && hideHandle.remove();
            };
        }

        const viewport = window.visualViewport;
        if (!viewport) return;

        const handleViewportChange = () => {
            const offset = Math.max(window.innerHeight - viewport.height - viewport.offsetTop, 0);
            setKeyboardOffset(offset);
        };

        viewport.addEventListener('resize', handleViewportChange);
        viewport.addEventListener('scroll', handleViewportChange);
        handleViewportChange();

        return () => {
            viewport.removeEventListener('resize', handleViewportChange);
            viewport.removeEventListener('scroll', handleViewportChange);
        };
    }, []);

    const handleLoginResponse = useCallback((response) => {
        if (response?.twoFARequired) {
            navigate('/verify-2fa', {
                state: {
                    userId: response.userId,
                }
            });
        } else if (response?.user) {
            const superAdminEmail = (process.env.REACT_APP_SUPER_ADMIN_EMAIL || '').trim().toLowerCase();
            const userEmail = (response.user.email || '').trim().toLowerCase();
            
            if ((superAdminEmail && userEmail === superAdminEmail) || response.user.role === 'introducer' || response.user.isIntroducer || response.user.company?.isIntroducer) {
                navigate('/owner-dashboard');
            } else {
                const defaultPath = response.user.role === 'customer'
                    ? '/customer-portal/dashboard'
                    : response.user.role === 'staff'
                    ? '/staff-dashboard'
                    : '/dashboard';
                navigate(defaultPath);
            }
        } else {
            setError('Login failed. Please try again.');
        }
    }, [navigate]);

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();

        // Fallback for Android autofill bypassing React state
        const currentEmail = email || document.getElementById('username')?.value || '';
        const currentPassword = password || document.getElementById('password')?.value || '';

        if (!executeRecaptcha) {
            setError('reCAPTCHA is not ready. Please wait a moment.');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const recaptchaToken = await executeRecaptcha('login');
            const response = await login(currentEmail.trim(), currentPassword, recaptchaToken);
            handleLoginResponse(response);
        } catch (err) {
            let errorMessage = err.response?.data?.message;
            if (!errorMessage && err.code) {
                switch (err.code) {
                    case 'auth/user-not-found':
                    case 'auth/wrong-password':
                    case 'auth/invalid-credential':
                        errorMessage = 'Invalid email or password.';
                        break;
                    case 'auth/invalid-email':
                        errorMessage = 'The email address is not valid.';
                        break;
                    case 'auth/user-disabled':
                        errorMessage = 'This account has been disabled.';
                        break;
                    case 'auth/network-request-failed':
                        errorMessage = 'Network error. Please check your internet connection.';
                        break;
                    default:
                        errorMessage = 'An unexpected error occurred. Please try again.';
                        break;
                }
            }
            if (!errorMessage) {
                 errorMessage = 'Login failed. Please try again.';
            }
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [executeRecaptcha, email, password, login, handleLoginResponse]);


    /*
    const handleGoogleLogin = useCallback(async () => {
        if (!executeRecaptcha) {
            setError('reCAPTCHA is not ready. Please wait a moment.');
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const recaptchaToken = await executeRecaptcha('login_google');
            const response = await googleLogin(recaptchaToken);
            handleLoginResponse(response);
        } catch (err) {
            let errorMessage = err.response?.data?.message;
            if (!errorMessage && err.code) {
                switch (err.code) {
                    case 'auth/popup-closed-by-user':
                    case 'auth/cancelled-popup-request':
                        setLoading(false);
                        return;
                    case 'auth/operation-not-allowed':
                        errorMessage = 'Google Sign-In is not enabled for this app.';
                        break;
                    default:
                        errorMessage = 'An unexpected error occurred during Google sign-in.';
                        break;
                }
            }
            if (!errorMessage) {
                errorMessage = 'Google login failed. Please try again.';
            }
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [executeRecaptcha, googleLogin, handleLoginResponse]);
    */


    return (
        <div
            className="w-full flex items-center justify-center bg-gray-900 p-4 overflow-y-auto"
            style={{ minHeight: `calc(100dvh - ${keyboardOffset}px)` }}
        >
             <div className="absolute inset-0 z-0">
                <div className="area" style={{ background: "linear-gradient(135deg,#0b1730 0%,#12306b 55%,#0e7490 100%)" }}>
                    <ul className="circles">
                        <li></li>
                        <li></li>
                        <li></li>
                        <li></li>
                        <li></li>
                        <li></li>
                        <li></li>
                        <li></li>
                        <li></li>
                        <li></li>
                    </ul>
                </div>
            </div>
            <div className="relative z-10 bg-white/10 backdrop-blur-md p-8 sm:p-10 rounded-2xl shadow-2xl border border-white/20 w-full max-w-md">
                 <div className="flex justify-center mb-8">
                     <img src="frontpagelogo.png" alt="Company Logo" className="w-24 h-24 bg-transparent" />
                 </div>
                <h2 className="text-3xl font-extrabold text-center text-white mb-2">Welcome Back</h2>
                <p className="text-center text-gray-300 mb-8">Sign in to continue to The One</p>

                {error && (
                    <div className="bg-red-500/30 border border-red-500 text-white px-4 py-3 rounded-lg mb-6 text-sm" role="alert">
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6" method="POST">
                    <div>
                        <label htmlFor="username" className="sr-only">
                            Email address
                        </label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Mail className="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                                type="email"
                                id="username"
                                name="username"
                                autoComplete="username"
                                className="appearance-none relative block w-full pl-10 pr-4 py-3 bg-gray-800/50 border border-white/20 rounded-md shadow-sm placeholder-gray-400 text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900 focus:ring-sky-500 sm:text-sm transition duration-150 ease-in-out"
                                placeholder="you@example.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                            />
                        </div>
                    </div>
                    <div>
                        <label htmlFor="password" className="sr-only">
                            Password
                        </label>
                        <div className="relative">
                             <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Lock className="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                                type="password"
                                id="password"
                                name="password"
                                autoComplete="current-password"
                                className="appearance-none relative block w-full pl-10 pr-4 py-3 bg-gray-800/50 border border-white/20 rounded-md shadow-sm placeholder-gray-400 text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900 focus:ring-sky-500 sm:text-sm transition duration-150 ease-in-out"
                                placeholder="••••••••"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                            />
                        </div>
                    </div>

                    <div className="text-sm text-right">
                        <Link to="/forgot-password" className="font-medium text-sky-400 hover:text-sky-300 transition duration-150 ease-in-out">
                            Forgot password?
                        </Link>
                    </div>

                    <button
                        type="submit"
                        className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-semibold rounded-md text-white bg-sky-600 hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900 focus:ring-sky-500 transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed"
                        disabled={loading || !executeRecaptcha}
                    >
                        {loading ? 'Logging in...' : 'Sign in'}
                    </button>
                </form>

                {/* <div className="relative my-8">
                    <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-white/20"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                        <span className="px-3 bg-gray-900/80 text-gray-400 rounded-full">Or continue with</span>
                    </div>
                </div>

                <button
                    onClick={handleGoogleLogin}
                    className="group relative w-full flex justify-center items-center py-3 px-4 border border-white/20 text-sm font-semibold rounded-md text-white bg-white/10 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900 focus:ring-sky-500 transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed"
                    disabled={loading || !executeRecaptcha}
                >
                    <FcGoogle className="h-5 w-5 mr-3" />
                    {loading ? 'Signing in...' : 'Sign in with Google'}
                </button>
                */}
            </div>
        </div>
    );
};

export default LoginPage;